import { useCallback, useLayoutEffect, useRef } from 'react';
import { Composer, type ComposerProps } from 'react-native-gifted-chat';

import { MAX_COMPOSER_HEIGHT, MIN_COMPOSER_HEIGHT } from './composerHeights';

type WebComposerProps = ComposerProps & {
  /** While a send is in flight, force the textarea to the empty-composer
   *  height so the disabled state doesn't visually retain the just-sent
   *  message's full content height. */
  isSending?: boolean;
};

/**
 * Web-specific Composer wrapper that auto-sizes the textarea based on content.
 * Works around gifted-chat's fixed `height: composerHeight` which prevents
 * the textarea from shrinking when text is deleted on web.
 * Resizes on text change by resetting height to auto, measuring scrollHeight,
 * then setting measured height clamped to [MIN_COMPOSER_HEIGHT, MAX_COMPOSER_HEIGHT].
 * Internal overflow scrolling kicks in past the max. Captures the DOM textarea
 * ref from the layout event target.
 */
export function WebComposer({ isSending = false, ...props }: WebComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Remembered height of the empty composer. We can't derive this from
   * MIN_COMPOSER_HEIGHT alone — the textarea's actual empty scrollHeight is
   * higher because of padding + line-height (~56px in current styles), so
   * forcing MIN on send would visibly bounce to that natural height when
   * the send completes. Snapshotting the measured empty height the first
   * time we render with no text keeps the during-send and post-send heights
   * identical.
   */
  const emptyHeightRef = useRef<number>(MIN_COMPOSER_HEIGHT);

  const setTextareaRef = useCallback(
    (node: unknown) => {
      const currentRef = node as
        | HTMLTextAreaElement
        | { _node?: HTMLTextAreaElement | null }
        | null;

      if (currentRef instanceof HTMLTextAreaElement) {
        textareaRef.current = currentRef;
      } else if (currentRef?._node instanceof HTMLTextAreaElement) {
        textareaRef.current = currentRef._node;
      }

      textareaRef.current?.classList.add('no-scrollbar');

      const existingRef = (props.textInputProps as { ref?: React.Ref<unknown> })?.ref;

      if (typeof existingRef === 'function') {
        existingRef(node);
      } else if (existingRef && typeof existingRef === 'object' && 'current' in existingRef) {
        existingRef.current = node;
      }
    },
    [props.textInputProps]
  );

  /**
   * useLayoutEffect (not useEffect) so the height change applies before paint.
   * Using a deferred effect leaves a one-frame gap where the textarea is still
   * at its previous height (e.g. multiline message height) when isSending
   * flips true, producing a visible jump just before the collapse to MIN.
   */
  useLayoutEffect(() => {
    const el = textareaRef.current;

    if (!el) {
      return;
    }

    if (isSending) {
      el.style.height = `${emptyHeightRef.current}px`;

      return;
    }

    el.style.height = 'auto';

    const scrollHeight = el.scrollHeight;

    const clampedHeight = Math.min(
      Math.max(scrollHeight, MIN_COMPOSER_HEIGHT),
      MAX_COMPOSER_HEIGHT
    );

    if (!props.text) {
      emptyHeightRef.current = clampedHeight;
    }

    el.style.height = `${clampedHeight}px`;
  }, [props.text, isSending]);

  const textInputPropsWithRef = {
    ...props.textInputProps,
    ref: setTextareaRef,
  };

  return <Composer {...props} textInputProps={textInputPropsWithRef} />;
}
